/**
 * The four cookie-consent categories (features/privacy-and-consent.md, §C.12). `strictlyNecessary` is
 * always on (no opt-out — the booking works on this alone). The other three are opt-in and gate,
 * respectively, functional storage, the analytics adapter (no PostHog before GRANTED), and
 * third-party embeds (the click-to-load `ConsentGate`, e.g. the OSM map).
 */
export type ConsentCategory = "strictlyNecessary" | "functional" | "analytics" | "thirdPartyEmbeds";

/** The three categories a diner can actually toggle (strictly-necessary is implied). */
export const OPTIONAL_CATEGORIES: readonly ConsentCategory[] = [
  "functional",
  "analytics",
  "thirdPartyEmbeds",
] as const;

export const ALL_CATEGORIES: readonly ConsentCategory[] = [
  "strictlyNecessary",
  ...OPTIONAL_CATEGORIES,
] as const;

/** A per-category decision map. `strictlyNecessary` is always `true`. */
export type ConsentDecision = Record<ConsentCategory, boolean>;

/**
 * The client-side receipt stored in first-party localStorage (Tech decisions): the categories chosen,
 * the `policyVersion` in force, and the timestamp. NO IP/fingerprint — it only proves a choice was
 * made. Re-prompted after {@link RECEIPT_VALIDITY_MS} (6 months, CNIL) or on a `policyVersion` change.
 */
export interface ConsentReceipt {
  decision: ConsentDecision;
  policyVersion: string;
  /** Epoch milliseconds when the choice was recorded. */
  recordedAt: number;
}

/** 6-month validity (CNIL consent-refresh recommendation), NOT the 13-month cookie-lifetime ceiling. */
export const RECEIPT_VALIDITY_MS = 183 * 24 * 60 * 60 * 1000;

/** The localStorage key the receipt is stored under (first-party, per origin). */
export const RECEIPT_STORAGE_KEY = "consent.receipt.v1";

/** Every optional category refused; strictly-necessary on. The reject-all baseline (R1). */
export function rejectAll(): ConsentDecision {
  return {
    strictlyNecessary: true,
    functional: false,
    analytics: false,
    thirdPartyEmbeds: false,
  };
}

/** Every category accepted. The accept-all shortcut. */
export function acceptAll(): ConsentDecision {
  return {
    strictlyNecessary: true,
    functional: true,
    analytics: true,
    thirdPartyEmbeds: true,
  };
}
